import { getFirebaseAuth } from '@/lib/firebase'
import { signOut as firebaseSignOut } from 'firebase/auth'
import { env } from '@/lib/env'
import { clearDisclaimerAcceptance } from '@/lib/disclaimer'

export const API_BASE = env.API_URL

// Guards against firing multiple redirects when several in-flight requests all
// come back 401 at once.
let redirectingToLogin = false

/**
 * Centralized response to an unauthorized (401 / UNAUTHENTICATED) API result.
 *
 * Signs the user out of Firebase — so app state reflects the rejected token and
 * /login won't bounce a "still signed-in" user straight back to /chat — then
 * redirects to /login via a full navigation (clears any stale in-memory state).
 *
 * No-ops on the server, on the public /shared/* pages, and when already on
 * /login, so it's safe to call from every API path (REST `unwrap` + SSE).
 */
export function handleUnauthorized(): void {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  if (path === '/login' || path.startsWith('/shared/')) return
  if (redirectingToLogin) return
  redirectingToLogin = true

  // Best-effort sign-out; the redirect happens regardless.
  clearDisclaimerAcceptance()
  void getFirebaseAuth()
    .then((auth) => {
      if (auth) firebaseSignOut(auth).catch(() => {})
    })
    .catch(() => {})
  window.location.replace('/login')
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const auth = await getFirebaseAuth().catch((err) => {
    console.error('[apiClient] Failed to initialize Firebase:', err)
    return null
  })
  const user = auth?.currentUser
  if (!user) return {}

  try {
    const token = await user.getIdToken()
    return { Authorization: `Bearer ${token}` }
  } catch (err) {
    console.error('[apiClient] Failed to get ID token — signing out:', err)
    clearDisclaimerAcceptance()
    if (auth) {
      await firebaseSignOut(auth).catch((signOutErr) => {
        console.error('[apiClient] Failed to sign out after token error:', signOutErr)
      })
    }
    return {}
  }
}

/**
 * Structured API error.
 *
 * The backend returns a flat error envelope on every non-2xx response:
 *   { "code": "...", "message": "...", "retryable": bool, "request_id": "..." }
 *
 * code:      machine-readable string (e.g. 'UNAUTHENTICATED', 'NOT_FOUND')
 * message:   human-readable description
 * status:    HTTP status code
 * retryable: whether the caller can safely retry
 */
export class ApiError extends Error {
  code: string
  status: number
  retryable: boolean

  constructor(code: string, message: string, status: number, retryable = false) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.retryable = retryable
  }
}

/**
 * Map HTTP status → error code for the fallback case where the response
 * isn't the standard flat envelope (e.g. an HTML error page, a proxy 5xx).
 */
export function statusToCode(status: number): string {
  switch (status) {
    case 401:
      return 'UNAUTHENTICATED'
    case 403:
      return 'FORBIDDEN'
    case 404:
      return 'NOT_FOUND'
    case 405:
      return 'METHOD_NOT_ALLOWED'
    case 400:
    case 422:
      return 'VALIDATION_FAILED'
    case 409:
      return 'CONFLICT'
    case 429:
      return 'RATE_LIMITED'
    default:
      return status >= 500 ? 'INTERNAL_ERROR' : 'HTTP_ERROR'
  }
}

/**
 * Whether a status is safe to retry when the response body doesn't carry an
 * explicit `retryable` flag. Mirrors the backend's code table: 429 (rate
 * limited) and 5xx (transient model/retrieval/infra errors) are retryable.
 */
export function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500
}

/**
 * Bridge the generated client's `{ data, status }` response into the app's
 * thrown-`ApiError` convention.
 *
 * - 2xx → returns `data` (204 already arrives as `null` from the mutator).
 * - non-2xx → throws `ApiError`, preferring the flat envelope's
 *   `code`/`message`/`retryable` and falling back to status-derived values.
 *
 * Every domain wrapper in `src/api/*` runs its generated call through this so
 * consumers keep a single error type to `catch`.
 */
export function unwrap<T>(res: { data: unknown; status: number }): T {
  if (res.status >= 200 && res.status < 300) {
    return res.data as T
  }

  const body = (res.data ?? null) as {
    code?: string
    message?: string
    retryable?: boolean
  } | null

  const code = body?.code ?? statusToCode(res.status)
  const message = body?.message ?? `HTTP ${res.status}`
  const retryable =
    typeof body?.retryable === 'boolean' ? body.retryable : isRetryableStatus(res.status)

  // Any unauthorized response sends the user to /login (see handleUnauthorized).
  if (code === 'UNAUTHENTICATED') {
    handleUnauthorized()
  }

  throw new ApiError(code, message, res.status, retryable)
}
